import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import QRCode from "qrcode";
import { Copy, Download } from "lucide-react";
import { apiBaseUrl } from "../../api/http.js";
import { useSecretCopy } from "../../app/clipboard.js";
import { isDesktop } from "../../app/platform.js";
import { buildEmergencyKitPdf, emergencyKitFileName } from "../../lib/emergencyKit.js";
import { formatDateTime } from "../../lib/format.js";
import { Checkbox } from "../../ui/forms.js";
import { Alert, Button } from "../../ui/primitives.js";

/** Where the kit says to sign in: this site in the browser, the configured server in the desktop app. */
function kitAppUrl(): string {
  return isDesktop() ? apiBaseUrl() : window.location.origin;
}

/**
 * Shows a freshly created recovery key once, with its QR code and the PDF download. The parent
 * only lets the user go on after confirming the kit is saved.
 */
export function EmergencyKitView(props: {
  readonly email: string;
  readonly recoveryKey: string;
  readonly createdAt: number;
  readonly saved: boolean;
  readonly onSaved: (saved: boolean) => void;
}): ReactNode {
  const copy = useSecretCopy();
  const [qr, setQr] = useState<string | undefined>(undefined);
  const [downloaded, setDownloaded] = useState(false);

  useEffect((): (() => void) => {
    let cancelled = false;
    void QRCode.toString(props.recoveryKey, { type: "svg", margin: 1, errorCorrectionLevel: "M", color: { dark: "#03161B", light: "#E9F4F1" } })
      .then((svg: string): void => {
        if (!cancelled) {
          setQr(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
        }
      })
      .catch((): void => undefined);
    return (): void => {
      cancelled = true;
    };
  }, [props.recoveryKey]);

  function download(): void {
    const bytes = buildEmergencyKitPdf({
      email: props.email,
      appUrl: kitAppUrl(),
      recoveryKey: props.recoveryKey,
      createdAt: formatDateTime(props.createdAt)
    });
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = emergencyKitFileName(props.email);
    anchor.click();
    window.setTimeout((): void => URL.revokeObjectURL(url), 1000);
    setDownloaded(true);
  }

  return (
    <div className="flex flex-col gap-5">
      <Alert tone="info">Speichere das Notfall-Kit jetzt. Der Schlüssel wird nur dieses eine Mal angezeigt; der Server kennt ihn nicht.</Alert>
      <div className="flex flex-col gap-4 rounded-lg border border-line bg-ink p-4 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-mist-2">Wiederherstellungsschlüssel</p>
          <p className="mt-2 grid grid-cols-4 gap-x-3 gap-y-1 font-mono text-[0.95rem] tracking-wide text-foam" data-testid="recovery-key">
            {props.recoveryKey.split("-").map((group: string, index: number) => (
              <span key={index}>{group}</span>
            ))}
          </p>
        </div>
        {qr !== undefined ? (
          <img src={qr} alt="QR-Code des Wiederherstellungsschlüssels" className="size-32 shrink-0 self-center rounded-md" />
        ) : null}
      </div>
      <div className="flex flex-wrap gap-3">
        <Button icon={Download} onClick={download}>
          PDF herunterladen
        </Button>
        <Button variant="secondary" icon={Copy} onClick={(): void => void copy(props.recoveryKey, "Schlüssel kopiert")}>
          Schlüssel kopieren
        </Button>
      </div>
      <p className="text-sm leading-relaxed text-mist">
        Drucke das PDF aus und bewahre es getrennt von deinen Geräten auf. Wer den Schlüssel und deinen zweiten Faktor hat, kann deinen Tresor öffnen.
      </p>
      <Checkbox id="kit-saved" checked={props.saved} onChange={props.onSaved}>
        {downloaded ? "Ich habe das Notfall-Kit gespeichert und bewahre es sicher auf." : "Ich habe den Schlüssel sicher gespeichert oder notiert."}
      </Checkbox>
    </div>
  );
}
